/**
 * The registration lifecycle states, and the dictionary key that labels each.
 *
 * `Registration.status` *is* the payment state — there is no separate payment
 * entity — so these seven values are what an organizer reads on the
 * Registrations page and in the analytics chart alike. Both screens used to
 * carry their own copy of the labels, which is how `PAID` ended up with two
 * different Hungarian words ("Fizetett" and "Fizetve") in two files. One map,
 * one dictionary section, so they cannot drift again.
 *
 * The key is spelled out as a literal rather than built from the enum value,
 * because that is what lets `t()` and the dictionary tests see it.
 */
export type RegistrationStatus =
  | 'REGISTERED'
  | 'APPROVED'
  | 'TICKET_REQUESTED'
  | 'PAYMENT_INSTRUCTIONS_SENT'
  | 'PAID'
  | 'PAYMENT_EXPIRED'
  | 'CANCELLED';

export const REGISTRATION_STATUS_LABEL_KEYS = {
  REGISTERED: 'registrationStatus.registered',
  APPROVED: 'registrationStatus.approved',
  TICKET_REQUESTED: 'registrationStatus.ticketRequested',
  PAYMENT_INSTRUCTIONS_SENT: 'registrationStatus.paymentInstructionsSent',
  PAID: 'registrationStatus.paid',
  PAYMENT_EXPIRED: 'registrationStatus.paymentExpired',
  CANCELLED: 'registrationStatus.cancelled',
} as const satisfies Record<RegistrationStatus, string>;

/** The states in lifecycle order — the order a status filter should offer them in. */
export const REGISTRATION_STATUSES = Object.keys(
  REGISTRATION_STATUS_LABEL_KEYS
) as RegistrationStatus[];

/**
 * The key type, kept as the literal union rather than widened to `string`.
 *
 * `t()` in this project only accepts keys it can see in the dictionary, so
 * widening here would not merely lose precision — it would fail to compile at
 * every call site. That check is worth keeping: it is the compiler verifying
 * that all seven labels exist in `hu.json` and `en.json`.
 */
export type RegistrationStatusLabelKey =
  (typeof REGISTRATION_STATUS_LABEL_KEYS)[RegistrationStatus];

/**
 * The dictionary key for a status, or `null` if the value is not one we know.
 *
 * A status arriving from the API that this build has never heard of returns
 * `null` so the caller can fall back to printing the raw value. That is worse
 * to read than a label, but it is honest, and it is what the page did for
 * every status before this map existed.
 *
 * `Object.hasOwn`, not `in`: the `in` operator walks the prototype chain, so a
 * status called `toString` or `constructor` would resolve to an inherited
 * member and hand `t()` a function instead of a key.
 */
export function registrationStatusLabelKey(status: string): RegistrationStatusLabelKey | null {
  return Object.hasOwn(REGISTRATION_STATUS_LABEL_KEYS, status)
    ? REGISTRATION_STATUS_LABEL_KEYS[status as RegistrationStatus]
    : null;
}
